import React from 'react';
import { Box, ButtonBase, IconButton, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { AppRoutes, getImageUrl } from '../../../constants/routes';
import { useMusicPlayer } from '../../../contexts/MusicPlayerContext';
import { useAuthProvider } from '../../../utils/auth';
import { claimReferral } from '../../../utils/referrals';

const TYPE_LABELS = { track: 'Song', album: 'Album', playlist: 'Playlist', act: 'Artist' };

export const musicRoute = (music) => {
  const identifier = music.slug || music.id;
  switch (music.type) {
    case 'album':
      return AppRoutes.AlbumPage.replace(':slug', identifier);
    case 'playlist':
      return AppRoutes.PlaylistView.replace(':id', music.id);
    case 'act':
      return AppRoutes.ActPage.replace(':slug', identifier);
    default:
      return AppRoutes.SinglePage.replace(':slug', identifier);
  }
};

const fallbackIcon = (type) => {
  if (type === 'playlist') return <QueueMusicIcon />;
  if (type === 'act') return <PersonIcon />;
  return <MusicNoteIcon />;
};

// The track / album / playlist / artist a post shares. Opening or playing it
// credits the post's author as the recommender (pass postId); without one -
// e.g. the composer's preview - it's display only.
const PostMusic = ({ music, postId }) => {
  const navigate = useNavigate();
  const { user } = useAuthProvider();
  const { playTrack } = useMusicPlayer();

  const credit = () => (postId && user ? claimReferral({ source: 'post', postId }) : Promise.resolve());

  const handleOpen = () => {
    if (!postId) return;
    credit();
    navigate(musicRoute(music));
  };

  const handlePlay = async (event) => {
    event.stopPropagation();
    if (!user) {
      navigate(AppRoutes.LoginPage);
      return;
    }
    // Record the recommendation before the play starts, so it's credited
    await credit();
    playTrack({ id: music.id, name: music.name, slug: music.slug, act_name: music.act_name, image: music.image });
  };

  const subtitle = [TYPE_LABELS[music.type], music.act_name].filter(Boolean).join(' · ');

  return (
    <ButtonBase
      component="div"
      onClick={handleOpen}
      disabled={!postId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        width: '100%',
        p: 1.5,
        mb: 2,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
        textAlign: 'left',
        justifyContent: 'flex-start',
        '&:hover': { borderColor: 'text.secondary' },
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          flexShrink: 0,
          borderRadius: music.type === 'act' ? '50%' : 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {music.image ? (
          <Box component="img" src={getImageUrl(music.image)} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : fallbackIcon(music.type)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
          {music.name}
        </Typography>
        <Typography variant="caption" noWrap component="div" sx={{ color: 'text.secondary' }}>
          {subtitle}
        </Typography>
      </Box>
      {music.type === 'track' && postId && (
        <IconButton
          onClick={handlePlay}
          aria-label={`Play ${music.name}`}
          sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}
        >
          <PlayArrowIcon />
        </IconButton>
      )}
    </ButtonBase>
  );
};

export default PostMusic;
